import { useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash/debounce";
import { AppDispatch, ActionType } from "../reducer";
import { AddVolumesAction } from "../reducer/volume";
import { Volume } from "../types/volume";
import { makeSelectVolume } from "../selectors/ui";
import { writeData } from "../api";
import { getVolume } from "../api/volume";
import { VolumeBlock } from "../types/notebook/note/block/volume";
import { loadKey, loadStatus } from "./loadStatus";
import { requireRecord, useTrackedLoad } from "./loadTracking";
import { useLoadError } from "../slices/ui";

export const useAddVolumes = () => {
  const dispatch = useDispatch<AppDispatch>();
  return (volumes: Volume[]) => {
    const action: AddVolumesAction = { type: ActionType.ADD_VOLUMES, volumes };
    dispatch(action as any);
  };
};
export const useVolume = (volumeId: string | undefined) => {
  const select = useMemo(() => makeSelectVolume(volumeId), [volumeId]);
  const volume = useSelector(select);
  const addVolumes = useAddVolumes();
  const writeBackend = useMemo(() => debounce((v: Volume) => { writeData(v); }, 500), [volumeId]);
  const setVolume = (v: Volume) => { addVolumes([v]); writeBackend(v); };
  const setField = (field: "name" | "description" | "size", value: string | number) => {
    if (!volume) return;
    const updated: Volume = {
      ...volume,
      "volume-spec": { ...volume["volume-spec"], [field]: value },
    };
    setVolume(updated);
  };
  return {
    volume,
    setName: (v: string) => { if (v.trim() === "") return; setField("name", v); },
    setDescription: (v: string) => setField("description", v),
    setSize: (v: number) => setField("size", v),
  };
};
export const useFetchVolume = () => {
  const addVolumes = useAddVolumes();
  return (block: VolumeBlock) =>
    getVolume({ "app-id": block["app-id"], "page-id": block["page-id"], "block-id": block["block-id"] })
      .then((v) => { if (v) addVolumes([v]); return v; });
};
// The block's record with an honest status. The fetch runs when the block's three ids are known; Retry re-runs it.
export const useVolumeLoad = (block: VolumeBlock | undefined) => {
  const fetch = useFetchVolume();
  const trackLoad = useTrackedLoad();
  const { volume } = useVolume(block?.["volume-id"]);
  const key = block ? loadKey("volume", block["block-id"]) : undefined;
  const error = useLoadError(key);
  const retry = () => (block && key ? trackLoad(key, requireRecord(fetch(block), "Volume")) : Promise.resolve(undefined));
  useEffect(() => {
    if (!block || !block["app-id"] || !block["page-id"] || !block["block-id"]) return;
    retry();
  }, [block?.["app-id"], block?.["page-id"], block?.["block-id"]]);
  return { volume, status: loadStatus(volume, error), retry };
};

export const useFetchVolumeListener = (block: VolumeBlock | undefined) => {
  useVolumeLoad(block);
};
